import type { ReactNode } from "react";
import { Button, EmptyState, MEyebrow, Spinner } from "@cntrl/ui";
import { ApiError } from "../../../app/hooks/apiError.js";
import styles from "./MobileHome.module.scss";

/**
 * The shared mobile home-card shell (mobile type + dashboard cards): a titled `<section>` with an
 * `MEyebrow` header and an optional trailing action. Every home widget renders inside one so the stack reads as one
 * system (`--m-*` tokens, radius-card, gutter). Chrome only — the widget owns its body + its four-state contract.
 */
export function MobileCard({
  title,
  action,
  bare,
  children,
}: {
  title: string;
  action?: ReactNode;
  /** Drop the white paper box (ART-176/178) — the eyebrow + body sit directly on the canvas, no border, no
   *  fill, no inset. The `.home` stack gap still separates it from its neighbours. Used by Opportunities and
   *  Travel, which the PO wants "with no box". */
  bare?: boolean;
  children: ReactNode;
}) {
  return (
    <section className={bare ? styles.bareCard : styles.card} aria-label={title}>
      <header className={styles.cardHead}>
        <MEyebrow>{title}</MEyebrow>
        {action != null && <div className={styles.cardAction}>{action}</div>}
      </header>
      <div className={styles.cardBody}>{children}</div>
    </section>
  );
}

/** The card loading state (a centered spinner) — the same posture as the desktop dashboard widgets. */
export function CardLoading() {
  return (
    <div className={styles.center} role="status" aria-live="polite">
      <Spinner />
    </div>
  );
}

/** The card error state with Retry — surfaces the server message verbatim, never a swallowed failure.*/
export function CardError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <EmptyState
      title="Couldn't load"
      description={error instanceof ApiError ? error.message : "Please try again."}
      action={
        <Button size="sm" onClick={onRetry}>
          Retry
        </Button>
      }
    />
  );
}
